// /src/components/TestAccessModal.jsx
import React from "react";
import { useTranslation } from "react-i18next";

export default function TestAccessModal({ open, onClose = () => {}, onRegister = () => {} }) {
  const { t } = useTranslation("common");
  const modalRef = React.useRef(null);

  const L = {
    header: t("test.header", "TEST"),
    title: t("test.title", "Спробуй Radiomonitor.net безкоштовно!"),
    subtitle: t(
      "test.subtitle",
      "Підключи свою станцію на 14 днів і переконайся особисто в ефективності сервісу."
    ),
    email_label: t(
      "test.email_label",
      "Для активації тестового періоду потрібно зареєструватися.\n\nЯкщо ви вже зареєструвалися, зайдіть, будь ласка, в розділ «Статистика» → «Налаштування» та додайте свою радіостанцію. Виконайте підключення і для вашої станції буде активовано тестовий період."
    ),
    placeholder: t("test.placeholder", "you@example.com"),
    send: t("test.send", "Перейти до реєстрації"),
    close: t("common.close", "Закрити"),
  };

  // === A11y: фокус в модалці, Esc для закриття, Tab-трап ===
  React.useEffect(() => {
    if (!open) return;
    const root = modalRef.current;
    if (!root) return;

    const prev = document.activeElement;
    const focusables = () =>
      Array.from(
        root.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )
      ).filter((el) => !el.hasAttribute("disabled") && !el.getAttribute("aria-hidden"));

    const list = focusables();
    if (list.length) list[0].focus();
    else {
      root.setAttribute("tabindex", "-1");
      root.focus();
    }

    const onKey = (e) => {
      if (e.key === "Escape") {
        try { onClose(); } catch {}
      } else if (e.key === "Tab") {
        const els = focusables();
        if (!els.length) return;
        const first = els[0];
        const last = els[els.length - 1];
        const active = document.activeElement;
        if (e.shiftKey) {
          if (active === first || !root.contains(active)) {
            last.focus();
            e.preventDefault();
          }
        } else {
          if (active === last) {
            first.focus();
            e.preventDefault();
          }
        }
      }
    };

    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      if (prev && typeof prev.focus === "function") prev.focus();
    };
  }, [open, onClose]);

  if (!open) return null;

  const handleSubmit = (e) => {
    e?.preventDefault?.();
    let shouldClose = true;
    try {
      const result = onRegister?.();
      if (result === false) shouldClose = false;
    } catch {
      // ignore handler errors, keep modal open
      shouldClose = false;
    }
    if (shouldClose) {
      try { onClose?.(); } catch {}
    }
  };

  const Header = (
    <div
      style={{
        background: "#057437",
        color: "#fff",
        padding: "20px 28px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        fontWeight: 700,
        fontSize: 22,
        letterSpacing: "0.4px",
        borderTopLeftRadius: 22,
        borderTopRightRadius: 22,
      }}
    >
      <span id="test-modal-title">{L.header}</span>
      <button
        onClick={onClose}
        type="button"
        aria-label={L.close}
        title={L.close}
        style={{
          background: "none",
          border: "none",
          color: "#fff",
          fontSize: 26,
          cursor: "pointer",
          marginRight: -4,
          marginTop: -4,
          fontWeight: 400,
        }}
      >
        ×
      </button>
    </div>
  );

  return (
    <div
      className="demo-overlay"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.28)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: "24px 16px",
      }}
      onClick={onClose}
    >
      <div
        className="demo-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="test-modal-title"
        aria-describedby="test-modal-body"
        ref={modalRef}
        style={{
          width: "min(92vw, 520px)",
          background: "#fff",
          borderRadius: 22,
          boxShadow: "0 22px 42px rgba(0,0,0,0.22)",
          overflow: "hidden",
          fontFamily: "Inter, Arial, sans-serif",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {Header}

        <div
          id="test-modal-body"
          style={{
            padding: "28px 28px 30px",
            color: "#111",
          }}
        >
          <form
            onSubmit={handleSubmit}
            style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0 }}
          >
            <h2
              style={{
                width: "100%",
                textAlign: "center",
                margin: "0 0 18px",
              }}
            >
              <span
                style={{
                  display: "inline-block",
                  maxWidth: 460,
                  fontSize: 19,
                  fontWeight: 700,
                  lineHeight: 1.32,
                  color: "#111",
                }}
              >
                {L.title}
              </span>
            </h2>
            <p
              style={{
                margin: "0 0 16px",
                fontSize: 15.5,
                lineHeight: 1.48,
                maxWidth: 430,
                textAlign: "left",
                marginLeft: "auto",
                marginRight: "auto",
              }}
            >
              {L.subtitle}
            </p>
            <p
              style={{
                margin: "0 0 24px",
                fontSize: 15.5,
                lineHeight: 1.48,
                maxWidth: 430,
                textAlign: "left",
                marginLeft: "auto",
                marginRight: "auto",
                whiteSpace: "pre-line",
              }}
            >
              {L.email_label}
            </p>

            <button
              type="submit"
              style={{
                width: "100%",
                maxWidth: 430,
                background: "#00723a",
                color: "#fff",
                border: "none",
                padding: "16px 18px",
                borderRadius: 12,
                fontSize: 18,
                fontWeight: 700,
                cursor: "pointer",
                boxShadow: "0 12px 26px rgba(0, 114, 58, 0.25)",
                transition: "transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease",
                marginLeft: "auto",
                marginRight: "auto",
              }}
            >
              {L.send}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
